/** Narrow SDK contract; no server credentials or project payloads enter the loader. */
export interface GoogleMapInstance {
  setCenter(position: { lat: number; lng: number }): void;
  setZoom(zoom: number): void;
  fitBounds(bounds: GoogleMapBounds, padding: number): void;
  addListener(event: string, handler: () => void): { remove(): void };
}

export interface GoogleMapBounds {
  extend(position: { lat: number; lng: number }): void;
}

export interface GoogleMapPoint {
  x: number;
  y: number;
}

export interface GoogleMapMarker extends HTMLElement {
  map: GoogleMapInstance | null;
}

export interface GoogleMapsApi {
  Geocoder: new () => {
    geocode(request: { address: string }): Promise<{ results: GoogleGeocodeResult[] }>;
  };
  Map: new (
    element: HTMLElement,
    options: {
      center: { lat: number; lng: number };
      zoom: number;
      minZoom: number;
      maxZoom: number;
      mapId?: string;
      colorScheme: string;
      streetViewControl: boolean;
      mapTypeControl: boolean;
      gestureHandling: string;
    },
  ) => GoogleMapInstance;
  Marker: new (options: {
    map: GoogleMapInstance;
    position: { lat: number; lng: number };
    title: string;
    optimized: boolean;
    zIndex?: number;
    label?: { text: string; color: string; fontSize: string; fontWeight: string };
    icon: {
      path: string;
      fillColor: string;
      fillOpacity: number;
      strokeColor: string;
      strokeWeight: number;
      scale: number;
      anchor?: GoogleMapPoint;
    };
  }) => {
    setMap(map: GoogleMapInstance | null): void;
    addListener(event: string, handler: () => void): { remove(): void };
  };
  LatLngBounds: new () => GoogleMapBounds;
  Point: new (x: number, y: number) => GoogleMapPoint;
  marker: {
    AdvancedMarkerElement: new (options: {
      map: GoogleMapInstance;
      position: { lat: number; lng: number };
      title: string;
      gmpClickable: boolean;
    }) => GoogleMapMarker;
    PinElement: new (options: {
      background: string;
      borderColor: string;
      glyphColor: string;
      scale: number;
      glyph?: string;
    }) => HTMLElement;
  };
  event: { clearInstanceListeners(instance: GoogleMapInstance): void };
}

export interface GoogleGeocodeResult {
  partial_match?: boolean;
  address_components: Array<{ long_name: string; short_name: string; types: string[] }>;
  geometry: { location: { lat(): number; lng(): number } };
}

interface MapsWindow extends Window {
  google?: { maps?: GoogleMapsApi };
  heliosGoogleMapsReady?: () => void;
  gm_authFailure?: () => void;
}

export const GOOGLE_MAPS_AUTH_FAILURE = 'helios:google-maps-auth-failure';
const CALLBACK = 'heliosGoogleMapsReady';
let pending: Promise<GoogleMapsApi> | undefined;
let loadedKey: string | undefined;
let authenticationFailed = false;

export function loadGoogleMaps(browserKey: string): Promise<GoogleMapsApi> {
  if (!browserKey.trim()) return Promise.reject(new Error('Google Maps browser key is missing.'));
  if (loadedKey && loadedKey !== browserKey) {
    return Promise.reject(new Error('Google Maps configuration changed. Reload the page.'));
  }
  if (authenticationFailed) {
    return Promise.reject(
      new Error(
        'Google Maps authorization failed. Check browser key restrictions, API activation, and billing, then reload.',
      ),
    );
  }
  if (pending) return pending;

  loadedKey = browserKey;
  const mapsWindow = window as MapsWindow;
  pending = new Promise<GoogleMapsApi>((resolve, reject) => {
    const script = document.createElement('script');
    const query = new URLSearchParams({
      key: browserKey,
      v: 'weekly',
      loading: 'async',
      libraries: 'maps,marker,geocoding',
      callback: CALLBACK,
      auth_referrer_policy: 'origin',
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${query.toString()}`;
    script.async = true;
    script.dataset.heliosGoogleMaps = 'true';
    const nonce = document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce;
    if (nonce) script.nonce = nonce;

    const cleanup = () => {
      window.clearTimeout(timer);
      script.onerror = null;
    };
    const fail = (message: string) => {
      cleanup();
      script.remove();
      pending = undefined;
      loadedKey = undefined;
      reject(new Error(message));
    };
    const timer = window.setTimeout(
      () =>
        fail(
          'Google Maps timed out. Check network access and the Content Security Policy, then retry.',
        ),
      20_000,
    );
    mapsWindow[CALLBACK] = () => {
      const maps = mapsWindow.google?.maps;
      if (!maps?.Map || !maps.marker?.AdvancedMarkerElement || !maps.marker.PinElement) {
        fail('Google Maps did not load its map and marker libraries.');
        return;
      }
      cleanup();
      resolve(maps);
    };
    const previousAuthFailure = mapsWindow.gm_authFailure;
    mapsWindow.gm_authFailure = () => {
      authenticationFailed = true;
      fail(
        'Google Maps authorization failed. Check browser key restrictions, API activation, and billing, then reload.',
      );
      window.dispatchEvent(new Event(GOOGLE_MAPS_AUTH_FAILURE));
      previousAuthFailure?.();
    };
    script.onerror = () =>
      fail(
        'Google Maps could not load. Check network access and the Content Security Policy, then retry.',
      );
    document.head.append(script);
  });
  return pending;
}
